const { Type } = require('@sinclair/typebox');
const { actionUtils } = require('@sstdev/lib_metadata-config');
const { moduleRegistration } = require('@sstdev/lib_metadata-config');

/** Builds schema fragments for the Form form container and its child elements. */
function asDataDictionary(hNode, context, parentAccessRestrictions) {
    //all forms are pretty much the same in this. no need to break it out per each for now.
    const { id, namespace, relation, children, allowRecordDeactivation, keepOpenAfterSubmit } = hNode;
    // use hNode.securityModifier to figure out access restrictions
    const accessRestrictions = actionUtils.getNodeAccessRestrictions(hNode, context, parentAccessRestrictions);
    // if the context role or feature flags does not result in access to this (or sub) component
    // don't include any part of it in the data dictionary.
    if (accessRestrictions.hidden) return [{}, {}];

    //let's find out what the children define....
    //Each child form element returns TypeBox schemas in _partialTypeDefinition
    let [_subDataDictionary = {}, _partialTypeDefinition = {}] = children?.reduce(
        ([sdd, ptd], child) => {
            const [_subDataDictionary = {}, _partialTypeDefinition = {}] = context.extractFromChild(
                child,
                context,
                accessRestrictions
            );
            return [
                moduleRegistration.mergeSubDataDictionaries(sdd, _subDataDictionary),
                moduleRegistration.mergePartialTypeDefinitions(ptd, _partialTypeDefinition)
            ];
        },
        [{}, {}]
    ) ?? [{}, {}];

    function findRemoveActionObservedLocation(children, context, parentAccessRestrictions) {
        for (const child of children || []) {
            if (!child || typeof child !== 'object') continue;

            const accessRestrictions = actionUtils.getNodeAccessRestrictions(child, context, parentAccessRestrictions);
            if (accessRestrictions.hidden || accessRestrictions.readOnly) continue;

            if (child.hNodeType === 'FormButton' && actionUtils.normalizeAction(child.formAction) === 'remove') {
                return child.blockId;
            }

            const descendantObservedLocation = findRemoveActionObservedLocation(
                child.children,
                context,
                accessRestrictions
            );
            if (descendantObservedLocation) {
                return descendantObservedLocation;
            }
        }

        return undefined;
    }

    const observedLocation = hNode.blockId;
    const removeObservedLocation = findRemoveActionObservedLocation(children, context, accessRestrictions);
    const actions = new Set();
    Object.values(_partialTypeDefinition || {}).forEach(schema => {
        const actionValue = schema && schema['x-forAction'];
        if (Array.isArray(actionValue)) {
            actionValue.filter(Boolean).forEach(a => actions.add(a));
        } else if (actionValue) {
            actions.add(actionValue);
        }
    });

    function suppressWritesForReadOnly(accessContext) {
        // Read-only UI should still contribute GET visibility, but must not contribute write caps.
        if (!accessRestrictions.readOnly) return accessContext;
        if (!accessContext || typeof accessContext !== 'object') return accessContext;

        const ensured = { ...accessContext };

        const baseObservedLocation =
            ensured.get?.observedLocation ||
            ensured.post?.observedLocation ||
            ensured.patch?.observedLocation ||
            observedLocation;

        ensured.get = {
            ...(ensured.get || {}),
            value: true,
            ...(baseObservedLocation ? { observedLocation: baseObservedLocation } : {})
        };
        ['post', 'patch', 'remove', 'put'].forEach(method => {
            ensured[method] = {
                ...(ensured[method] || {}),
                value: false,
                ...(baseObservedLocation ? { observedLocation: baseObservedLocation } : {})
            };
        });

        return ensured;
    }

    function normalizeRouteAccessHints(routeAccessHints) {
        if (!routeAccessHints || typeof routeAccessHints !== 'object') return undefined;

        const normalized = {};
        ['get', 'post', 'patch', 'remove'].forEach(method => {
            const hint = routeAccessHints[method];
            if (!hint) return;

            if (hint === true) {
                normalized[method] = {
                    value: true,
                    ...(observedLocation ? { observedLocation } : {})
                };
                return;
            }

            if (typeof hint === 'object' && hint.value) {
                normalized[method] = {
                    value: true,
                    ...((hint.observedLocation || observedLocation)
                        ? { observedLocation: hint.observedLocation || observedLocation }
                        : {})
                };
            }
        });

        if (!Object.keys(normalized).length) return undefined;

        if (accessRestrictions.readOnly) return undefined;

        return normalized;
    }

    function orAccessContexts(contexts) {
        const acc = {
            get: { value: true, observedLocation },
            post: { value: false, observedLocation },
            patch: { value: false, observedLocation },
            put: { value: false, observedLocation },
            remove: { value: false, observedLocation }
        };

        (contexts || []).filter(Boolean).forEach(ctx => {
            if (ctx.post?.value) {
                acc.post.value = true;
                acc.post.observedLocation = acc.post.observedLocation || ctx.post.observedLocation;
            }
            if (ctx.patch?.value) {
                acc.patch.value = true;
                acc.patch.observedLocation = acc.patch.observedLocation || ctx.patch.observedLocation;
            }
            if (ctx.remove?.value) {
                acc.remove.value = true;
                acc.remove.observedLocation = acc.remove.observedLocation || ctx.remove.observedLocation;
            }
        });

        return acc;
    }

    function cloneSchema(schema) {
        if (!schema || typeof schema !== 'object') return schema;
        return Object.defineProperties({}, Object.getOwnPropertyDescriptors(schema));
    }

    function orRouteAccessHints(hintsList) {
        const merged = {};

        (hintsList || []).filter(Boolean).forEach(hints => {
            ['get', 'post', 'patch', 'remove'].forEach(method => {
                if (!hints[method]?.value) return;
                merged[method] = merged[method] || hints[method];
            });
        });

        return Object.keys(merged).length ? merged : undefined;
    }

    function buildPropertiesForAction(action) {
        return Object.entries(_partialTypeDefinition || {}).reduce((acc, [name, schema]) => {
            if (!schema) return acc;

            // Get explicit requiredActions, or infer from x-forAction if empty
            let requiredActions = schema['x-requiredActions'] || [];
            if (requiredActions.length === 0 && schema['x-forAction']) {
                // If x-requiredActions is empty but x-forAction is set, infer required actions from forAction
                const forActions = Array.isArray(schema['x-forAction'])
                    ? schema['x-forAction']
                    : [schema['x-forAction']];
                requiredActions = forActions.filter(Boolean);
            }

            let applies =
                requiredActions.length === 0 ||
                (action && requiredActions.includes(action)) ||
                (requiredActions.includes('create') && requiredActions.includes('update'));

            // Foreign key fields (format: namespace:relation) should always be optional
            // in the TypeBox schema to avoid them being added to JSON Schema required array
            // They're handled separately via x-required metadata
            if (name.includes(':')) {
                applies = false;
            }

            let propSchema = schema;
            if (!applies) {
                const optionalSchema = cloneSchema(schema);
                if (!name.includes(':')) {
                    optionalSchema['x-required'] = false;
                    optionalSchema['x-optional'] = true;
                }
                propSchema = Type.Optional(optionalSchema);
            }

            acc[name] = propSchema;
            return acc;
        }, {});
    }

    function buildRelationSchema(action) {
        const properties = buildPropertiesForAction(action);

        // When allowRecordDeactivation is true, add an 'active' boolean property
        // This allows records to be soft-deleted/deactivated rather than permanently deleted
        if (allowRecordDeactivation) {
            properties.active = Type.Optional(
                Type.Boolean({
                    title: 'Active',
                    'x-propertyName': 'active',
                    'x-dataType': 'boolean',
                    'x-editable': true,
                    description: 'Indicates whether this record is active (soft delete support)'
                })
            );
        }

        const relationAccessContext = actionUtils.buildAccessContextForAction(action, observedLocation);
        if (removeObservedLocation) {
            relationAccessContext.remove = {
                value: true,
                observedLocation: removeObservedLocation
            };
        }

        const accessContext = suppressWritesForReadOnly(relationAccessContext);
        const routeAccessHints = normalizeRouteAccessHints(hNode.routeAccessHints);
        return Type.Object(
            {
                _id: Type.Optional(Type.String({ title: 'Record ID', 'x-propertyName': '_id' })),
                ...properties
            }, // plain map: prop -> TypeBox schema
            {
                title: relation,
                // setting additionalProperties to false enforces strict validation: only explicitly defined form properties allowed
                additionalProperties: false,
                // keep access info as schema extension (optional but useful to consumers)
                ...(action ? { 'x-forAction': actionUtils.normalizeAction(action) } : {}),
                'x-accessRestrictions': accessRestrictions,
                'x-accessContext': accessContext,
                // x-routeAccessHints lets blocks request narrow route/OpenAPI recovery
                // without changing the broader meaning of x-accessContext.
                ...(routeAccessHints ? { 'x-routeAccessHints': routeAccessHints } : {}),
                // Include form behavior flags in schema metadata
                ...(keepOpenAfterSubmit ? { 'x-keepOpenAfterSubmit': true } : {})
            }
        );
    }

    const relationSchemas = [];
    if (!actions.size) {
        relationSchemas.push(buildRelationSchema());
    } else {
        if (actions.has('create')) {
            relationSchemas.push(buildRelationSchema('create'));
        }
        if (actions.has('update')) {
            relationSchemas.push(buildRelationSchema('update'));
        }
    }

    let relationSchema;
    if (relationSchemas.length === 1) {
        relationSchema = relationSchemas[0];
    } else {
        const wrapperAccessContext = suppressWritesForReadOnly(
            orAccessContexts(relationSchemas.map(schema => schema && schema['x-accessContext']))
        );
        const wrapperRouteAccessHints = orRouteAccessHints(
            relationSchemas.map(schema => schema && schema['x-routeAccessHints'])
        );

        relationSchema = {
            anyOf: relationSchemas,
            'x-accessRestrictions': accessRestrictions,
            'x-accessContext': wrapperAccessContext,
            ...(wrapperRouteAccessHints ? { 'x-routeAccessHints': wrapperRouteAccessHints } : {})
        };
    }

    const requiredForAllActions = Object.entries(_partialTypeDefinition || {}).reduce((req, [name, schema]) => {
        if (!schema || !schema['x-required']) return req;

        // Skip foreign key fields (format: namespace:relation) from automatic required inclusion
        // These are references to other entities and should not be in the universal required array
        // unless they're required across ALL forms (which is tracked at the merge level)
        if (name.includes(':')) return req;

        // Get explicit requiredActions, or infer from x-forAction if empty
        let requiredActions = schema['x-requiredActions'] || [];
        if (requiredActions.length === 0 && schema['x-forAction']) {
            // If x-requiredActions is empty but x-forAction is set, infer required actions from forAction
            const forActions = Array.isArray(schema['x-forAction']) ? schema['x-forAction'] : [schema['x-forAction']];
            requiredActions = forActions.filter(Boolean);
        }

        // A field is required for all actions only if:
        // 1. It has no action restrictions (no x-forAction and no x-requiredActions), OR
        // 2. It's explicitly required for BOTH create AND update
        const requiresBoth =
            requiredActions.length === 0 || (requiredActions.includes('create') && requiredActions.includes('update'));
        if (requiresBoth) {
            req.push(name);
        }
        return req;
    }, []);

    if (requiredForAllActions.length) {
        relationSchema.required = requiredForAllActions.sort();
    }

    //now, whatever _partialTypeDefinition was returned (TypeBox schemas), goes in our namespace/relation
    const subDataDictionary = moduleRegistration.mergeSubDataDictionaries(
        {
            [namespace]: {
                [relation]: {
                    ...relationSchema
                }
            }
        },
        _subDataDictionary
    );

    return [subDataDictionary, {}];
}

module.exports = { asDataDictionary };
